import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, X, ExternalLink, Bell } from "lucide-react";
import { BrandMark } from "./BrandLogo";
import LeftSidebar from "./LeftSidebar";
import RightSidebar from "./RightSidebar";
import { useAuth } from "../context/AuthContext";
import { WEBSITE_URL, fileUrl } from "../lib/api";

// Pages that show the right widgets sidebar
const SHOW_RIGHT_SIDEBAR_ON = ["/", "/chat", "/live", "/verlosung", "/mitglieder", "/rangliste"];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const showRight = SHOW_RIGHT_SIDEBAR_ON.includes(location.pathname);

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logout();
    navigate("/login");
  };

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="min-h-screen bg-[#F5F8FD] text-slate-900 grain flex flex-col">
      {/* Sticky glass header */}
      <header
        className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200"
        data-testid="app-header"
      >
        <div className="max-w-[1480px] mx-auto px-3 sm:px-5 py-2.5 sm:py-3 flex items-center gap-2">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="open-sidebar-drawer"
            aria-label="Menü öffnen"
            className="p-2 -ml-1 rounded-full text-slate-700 hover:bg-slate-100/70 lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
          <BrandMark className="flex-1 min-w-0" />
          <div className="flex items-center gap-2 shrink-0">
            {user && (
              <div className="hidden sm:flex items-center gap-2 bg-slate-100/70 border border-slate-200 rounded-full pl-1 pr-3 py-1">
                <img src={fileUrl(user.avatar_url)} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                <span className="text-xs font-semibold text-slate-900 max-w-[120px] truncate">{user.name}</span>
                {user.role === "admin" && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-[#1D5BFF]/15 text-[#1D5BFF] font-bold">Admin</span>
                )}
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              aria-label="Abmelden"
              title="Abmelden"
              className="p-2 rounded-full text-slate-700 hover:text-slate-900 hover:bg-slate-100/70 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* 3-column layout */}
      <div className="flex-1 max-w-[1480px] w-full mx-auto px-3 sm:px-5 py-5 pb-24 lg:pb-12 flex gap-5">
        {/* Left sidebar - desktop only */}
        <div className="hidden lg:block sticky top-[68px] self-start max-h-[calc(100vh-90px)] overflow-y-auto pr-1">
          <LeftSidebar />
        </div>

        {/* Main */}
        <main className="flex-1 min-w-0">{children}</main>

        {/* Right sidebar - desktop only on selected pages */}
        {showRight && (
          <div className="hidden xl:block sticky top-[68px] self-start max-h-[calc(100vh-90px)] overflow-y-auto pl-1">
            <RightSidebar />
          </div>
        )}
      </div>

      {/* Mobile drawer (left sidebar slides in) */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          data-testid="mobile-drawer"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeDrawer} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-[320px] bg-white border-r border-slate-200 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-4">
              <BrandMark />
              <button onClick={closeDrawer} className="p-2 rounded-full hover:bg-slate-100/70" data-testid="close-drawer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <LeftSidebar onNavigate={closeDrawer} />
          </div>
        </div>
      )}

      {/* Footer (desktop) */}
      <footer className="hidden lg:block border-t border-slate-200/60 mt-8 py-6">
        <div className="max-w-[1480px] mx-auto px-5 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} TSU-PARTNERS · Community für Fahrzeugüberführer</span>
          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="website-link"
            className="inline-flex items-center gap-1.5 text-[#4DBFFF] hover:text-slate-900 transition-colors font-semibold"
          >
            tsupartners.de <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </footer>
    </div>
  );
}
